// Actions contract row 9, Block time (Mike, 2026-09-24, answer 12, "You pick the time"): "Your free slots appear, and
// you choose one before anything is booked." Pressing a Block option opens this under the card: his free slots of the
// block's length, read from his own calendar (GET /api/triage/items/:id/slots), and any other time he types. Nothing is
// booked until he presses one; the press sends its start as `until`, and the API books exactly that.
import { ActionButton, ActionInput } from '../parts/index.js';
import { viewFetch as fetch } from '../view-as.js';
import { useEffect, useState } from 'react';
import { ApiError, Unauthenticated } from '../api.js';
import { dayMonthTime } from '../day.js';
import { blockSlots, type FreeSlot } from './api.js';

type Load = { kind: 'loading' } | { kind: 'ready'; slots: FreeSlot[] } | { kind: 'error'; said: string };

const pad = (n: number) => String(n).padStart(2, '0');
const localInput = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** The card's line once the block is in his calendar. */
export function bookedWord(r: { calendar?: { startsAt?: string; overlapsBusy?: boolean; did?: string } | null; needsGrant?: string | null }): string {
  if (r.needsGrant || r.calendar?.did !== 'block' || !r.calendar.startsAt) return 'Cobrain cannot write in your calendar yet, so nothing was booked. Block it in Outlook.';
  return `Booked ${dayMonthTime(new Date(r.calendar.startsAt))} in your calendar.${r.calendar.overlapsBusy ? ' It sits over something already there.' : ''}`;
}

/** Whether the press put a block in his calendar, so there is one for Undo to take out (BUGS.md T65). */
export function booked(r: { calendar?: { did?: string } | null; needsGrant?: string | null }): boolean {
  return !r.needsGrant && r.calendar?.did === 'block';
}

/**
 * Undo of a booked block (BUGS.md T65): POST /api/triage/decisions/:id/undo takes the block out of his calendar and puts the
 * card back. The block is in his own calendar and nobody was invited, so nothing went out and it can be taken back.
 */
export async function undoBlock(store: string, decisionId: string): Promise<void> {
  const res = await fetch(`/api/triage/decisions/${encodeURIComponent(decisionId)}/undo`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store }) });
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
}

export interface BlockBoxProps {
  store: string;
  passItemId: string;
  optionKey: string;
  busy?: boolean;
  error?: string | null;
  onBook: (at: Date) => void;
  onCancel: () => void;
}

export function BlockBox({ store, passItemId, optionKey, busy = false, error = null, onBook, onCancel }: BlockBoxProps) {
  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [other, setOther] = useState(() => { const d = new Date(Date.now() + 86_400_000); d.setHours(10, 0, 0, 0); return localInput(d); });
  useEffect(() => {
    let live = true;
    blockSlots(store, passItemId, optionKey)
      .then((r) => { if (live) setState({ kind: 'ready', slots: r.slots }); })
      .catch((e: unknown) => {
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        if (live) setState({ kind: 'error', said: e instanceof ApiError || e instanceof Error ? e.message : String(e) });
      });
    return () => { live = false; };
  }, [store, passItemId, optionKey]);
  const at = new Date(other);
  const valid = Number.isFinite(at.getTime()) && at.getTime() > Date.now();

  return (
    <div className="state" data-state="block">
      <p className="rule">Pick a time. Nothing is booked until you do.</p>
      {state.kind === 'loading' ? <p className="note">Reading your calendar…</p> : null}
      {state.kind === 'error' ? <p className="note">Cobrain could not read your free time: {state.said.replace(/\.$/, '')}. Pick a time below.</p> : null}
      {state.kind === 'ready' && state.slots.length === 0 ? <p className="note">Nothing is free in the next fortnight. Pick a time below.</p> : null}
      {state.kind === 'ready' && state.slots.length > 0 ? (
        <div className="choices" aria-label="your free slots">
          {state.slots.map((s) => (
            <ActionButton key={s.startsAt} type="button" className="choice" disabled={busy} onClick={() => onBook(new Date(s.startsAt))}>{dayMonthTime(new Date(s.startsAt))}</ActionButton>
          ))}
        </div>
      ) : null}
      <div className="choices">
        <div className="choice pick">
          <span>Another time…</span>
          <ActionInput type="datetime-local" aria-label="another time" value={other} min={localInput(new Date())} disabled={busy} onChange={(e) => setOther(e.target.value)} />
          <ActionButton type="button" className="btn small" disabled={busy || !valid} onClick={() => onBook(at)}>Book this time</ActionButton>
        </div>
      </div>
      <div className="teach-block">
        <ActionButton reading type="button" className="link" disabled={busy} onClick={onCancel}>Cancel</ActionButton>
      </div>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </div>
  );
}
